import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loading, Card, EmptyState } from "@/components/common";
import { fmtMeetingDate, MEETING_TYPE_LABELS } from "@/lib/meetingsApi";
import { ArrowLeft, Download, CalendarDays, MapPin, CheckCircle2, FileText } from "lucide-react";

export default function MeetingSummary() {
  const { id } = useParams();
  const [s, setS] = useState(undefined);
  useEffect(() => { api.get(`/meetings/${id}/summary`).then((r) => setS(r.data)).catch(() => setS(null)); }, [id]);
  if (s === undefined) return <Loading label="Loading summary…" />;
  if (!s) return <EmptyState icon={FileText} title="Summary unavailable" description="This meeting summary is not available." />;
  const m = s.meeting;
  return (
    <div data-testid="meeting-summary">
      <Link to={`/meetings/${id}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-navy mb-3"><ArrowLeft className="h-4 w-4" /> Meeting</Link>
      <Card className="mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-brand-civic">{m.governing_body_name}</span>
            <h1 className="font-display text-2xl font-extrabold text-brand-ink mt-1">{m.title} — Meeting Summary</h1>
            <p className="text-sm text-slate-500 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {fmtMeetingDate(m.date, m.start_time)}</span>
              {m.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {m.location}</span>}
              <span>{MEETING_TYPE_LABELS[m.meeting_type] || m.meeting_type}</span>
              {s.is_final ? <span className="inline-flex items-center gap-1 text-brand-teal font-semibold"><CheckCircle2 className="h-4 w-4" /> Completed</span> : <span className="text-brand-gold font-semibold">Draft (meeting not completed)</span>}
            </p>
          </div>
          <a data-testid="download-minutes-pdf" href={`${api.defaults.baseURL}/meetings/${id}/summary.pdf`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy text-white px-4 py-2 text-sm font-semibold hover:bg-brand-navy-900"><Download className="h-4 w-4" /> Download Minutes PDF</a>
        </div>
      </Card>

      <div className="grid gap-5 lg:grid-cols-3">
        <Card data-testid="summary-attendance">
          <h2 className="font-display font-bold text-brand-ink text-base mb-3">Attendance</h2>
          {(s.attendance || []).length === 0 ? <p className="text-sm text-slate-400">No attendance recorded.</p> : (
            <div className="space-y-1.5">
              {s.attendance.map((a, i) => (
                <div key={i} className="flex items-center justify-between text-sm rounded-lg border border-brand-line px-3 py-1.5">
                  <span className="text-brand-ink">{a.member_name}{a.role_label && <span className="text-slate-400"> · {a.role_label}</span>}</span>
                  <span className={`font-semibold capitalize ${a.attendance_status === "present" ? "text-brand-teal" : "text-slate-500"}`}>{a.attendance_status || "—"}</span>
                </div>
              ))}
            </div>
          )}
          {s.quorum && (
            <div className="mt-3 border-t border-brand-line pt-3 text-sm">
              <div className="font-semibold text-brand-ink">Quorum</div>
              <div className="text-slate-500">{s.quorum.present_voting} present · {s.quorum.required} required · <span className={s.quorum.met ? "text-brand-teal font-semibold" : "text-red-600 font-semibold"}>{s.quorum.met ? "Established" : "Not Established"}</span></div>
            </div>
          )}
        </Card>

        <Card className="lg:col-span-2" data-testid="summary-actions">
          <h2 className="font-display font-bold text-brand-ink text-base mb-3">Actions & Decisions</h2>
          {(s.actions || []).length === 0 ? <p className="text-sm text-slate-400">No formal actions were recorded.</p> : (
            <div className="space-y-3">
              {s.actions.map((a, i) => (
                <div key={i} className="rounded-lg border border-brand-line p-3" data-testid="summary-action">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-brand-navy">{a.number}</span>
                    <span className="text-brand-ink font-medium flex-1">{a.insight_title}</span>
                    <span className="inline-flex rounded-full border border-brand-teal/30 bg-brand-teal-50 px-2.5 py-0.5 text-xs font-semibold text-brand-teal">{a.outcome_label || "—"}</span>
                  </div>
                  {a.vote_totals && <div className="text-xs text-slate-500 mt-1.5">Vote — YES {a.vote_totals.YES} · NO {a.vote_totals.NO} · WITHHELD {a.vote_totals.WITHHELD}</div>}
                  {a.motion && <div className="text-xs text-slate-400 italic mt-1">Motion: {a.motion}{a.mover ? ` (Moved: ${a.mover}; Second: ${a.second})` : ""}</div>}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
